import { Alert, Box, Grid, Typography } from '@mui/material';
import React from 'react';
import TalentGroupedDocuments from './TalentGroupedDocuments';
import { talentGroupedDocuments, talentTypeOfDocuments } from './constants/documents';
import TalentDocuments from './TalentDocuments';
import { useGetTalent } from './components/useGetTalent';
import { useTalentOperations } from './context/TalentOperationsContext';
import { useParams } from 'react-router-dom';

interface TalentEditDocumentProps {
    talentId?: string;
}

const TalentEditDocument: React.FC<TalentEditDocumentProps> = ({ talentId: propTalentId }) => {
    // get talentId from props or fall back to route params
    const { id: paramTalentId } = useParams();
    const talentId = propTalentId ?? paramTalentId;
    const { talent, loading, error } = useGetTalent(talentId);

    // Show loading state
    if (loading) {
        return (
            <Box p={2} pt={0}>
                <Typography>Chargement des données du talent...</Typography>
            </Box>
        );
    }

    // Show error state
    if (error || !talent) {
        return (
            <Box p={2} pt={0}>
                <Typography color="error">
                    {error ? 'Erreur lors du chargement des données du talent' : 'Talent introuvable'}
                </Typography>
            </Box>
        );
    }
    return (
        <Box sx={{ background: '#222426', minHeight: '100vh', borderRadius: 4, p: { xs: 2, md: 3 } }}>
            <Grid
                container
                spacing={2}
                sx={{
                    width: '100%',
                }}>
                <Grid item xs={12}>
                    <Alert
                        severity="warning"
                        icon={<i className="fi-br-shield-exclamation" style={{ marginTop: 4 }} />}
                        sx={{
                            backgroundColor: 'rgba(242, 153, 74, 0.15)',
                            mb: 1.5,
                        }}>
                        <Typography
                            variant="h6"
                            fontWeight="700"
                            sx={{ mb: 0.5, color: theme => theme.palette.warning.main }}>
                            Mes documents
                        </Typography>
                        <Typography color="#F9F9F9" fontSize={'13px'}>
                            Cette espace a pour objectif de vous permettre de stocker en un seul endroit tous les documents
                            obligatoires lors d'une nouvelle embauche. Votre futur employeur pourra néanmoins vous demander
                            des documents complémentaires. Gagnez du temps et faites la différence avec les autres
                            candidats.
                        </Typography>
                    </Alert>
                </Grid>
                {/* Regular documents */}
                {talentTypeOfDocuments.map(({ type, label }) => {
                    return (
                        <Grid item xs={12} lg={6} key={type}>
                            <TalentDocuments
                                documents={talent?.files?.filter(({ context }) => context === type) ?? []}
                                documentType={type}
                                talentId={talentId}
                                isEdit
                            />
                        </Grid>
                    );
                })}

                {/* Grouped documents */}
                {talentGroupedDocuments.map(groupedDoc => {
                    // Prepare documents for this group
                    const groupDocuments = groupedDoc.documents.reduce((acc, doc) => {
                        acc[doc.type] = talent?.files?.filter(({ context }) => context === doc.type) ?? [];
                        return acc;
                    }, {} as Record<string, any[]>);

                    return (
                        <Grid item xs={12} key={groupedDoc.id} sx={{ display: 'flex' }}>
                            <TalentGroupedDocuments groupedDocument={groupedDoc} documents={groupDocuments} talentId={talentId} isEdit />
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

export default TalentEditDocument;
